const {
  hideElementsBySelector,
  showElementsBySelector,
  addVisibilityHiddenBySelector,
  removeVisibilityHiddenBySelector,
} = require('../common/utils/dom');

const stages = {
  CUSTOMER: 'customer',
  SHIPPING_METHOD: 'shippingMethod',
  SHIPPING_ADDRESS: 'shippingAddress',
  SHIPPING_SUMMARY: 'shippingSummary',
  PAYMENT_METHOD: 'paymentMethod',
  PAYMENT_DETAILS: 'payment', // Optional stage, only shown if needed
  BILLING_ADDRESS: 'billing', // Optional stage, only shown if needed
  PLACE_ORDER: 'placeOrder',
  SUBMITTED: 'submitted',
};
const stagesArray = Object.values(stages);

const stageParam = new URLSearchParams(window.location.search).get('stage');
const initialStage = stageParam !== null && isValidStage(stageParam)
  ? stageParam
  : stages.SHIPPING_METHOD;

let currentStage = initialStage;
let placeOrderReached = false;

function getStage() {
  return currentStage;
}

function isStageFromPaymentGroup(stage = currentStage) {
  const paymentStagesArray = [
    stages.PAYMENT_METHOD, stages.PAYMENT_DETAILS, stages.BILLING_ADDRESS, stages.PLACE_ORDER,
  ];
  return isValidStage(stage)
    && paymentStagesArray.includes(stage);
}

function isStageFromShippingGroup(stage = currentStage) {
  const shippingStagesArray = [
    stages.SHIPPING_METHOD, stages.SHIPPING_ADDRESS, stages.SHIPPING_SUMMARY,
  ];
  return isValidStage(stage)
    && shippingStagesArray.includes(stage);
}

function isValidStage(stage) {
  return stagesArray.includes(stage);
}

function handleHistoryChange(newStage, isInitialState) {
  const urlParams = new URLSearchParams(window.location.search);

  if (isInitialState) {
    // Start in SHIPPING_METHOD and then push stages until you reach the current one
    urlParams.set('stage', stages.SHIPPING_METHOD);
    window.history.replaceState(
      { stage: stages.SHIPPING_METHOD },
      document.title,
      `?${urlParams}`,
    );

    for (let i = 2; i <= Object.values(stages).indexOf(newStage); i++) {
      urlParams.set('stage', stages[Object.keys(stages)[i]]);
      window.history.pushState(
        { stage: stages[Object.keys(stages)[i]] },
        document.title,
        `?${urlParams}`,
      );
    }
  } else if (newStage !== currentStage) { // Push new state into history
    urlParams.set('stage', newStage);
    window.history.pushState(
      { stage: newStage },
      document.title,
      `?${urlParams}`,
    );
  } else if (!stageParam) { // newStage is same as current, but with no stageParam, then just add the stage into the url
    urlParams.set('stage', newStage);
    window.history.replaceState(
      { stage: newStage },
      document.title,
      `?${urlParams}`,
    );
  }
}

/* Set global variable if place order stage has been reached */
function setPlaceOrderReached(newStage) {
  if (!placeOrderReached) {
    placeOrderReached = newStage === stages.PLACE_ORDER;
  }
}

/* Checks if place order has been reached and flow is valid */
function checkIfGoToPlaceOrder(newStage) {
  const { areBillingAndPaymentValid } = require('./billing').methods;

  return placeOrderReached
  && (currentStage === stages.SHIPPING_SUMMARY || currentStage === stages.BILLING_ADDRESS)
  && isStageFromPaymentGroup(newStage)
  && areBillingAndPaymentValid();
}

/* Return to place order stage after editing shipping or billing */
function getNextStage(newStage) {
  const goToPlaceOrder = checkIfGoToPlaceOrder(newStage);

  return goToPlaceOrder ? stages.PLACE_ORDER : newStage;
}

/* Hides substages (onlyForm views which has no specific stage)
 * Current substages: Invoice and GiftMessage
 */
function hideSubStages() {
  const { hideInvoiceBlock } = require('./invoiceForm').methods;
  hideInvoiceBlock();

  const { hideGiftMessageBlock } = require('./shipping').methods;
  hideGiftMessageBlock();
}

function setStage(newStage, changeHistory = true, isInitialState = false) {
  if (isValidStage(newStage)) {
    setPlaceOrderReached(newStage);
    const nextStage = getNextStage(newStage);
    // If the navigation is done via browser navigation, skip this logic
    if (changeHistory) handleHistoryChange(nextStage, isInitialState);
    currentStage = nextStage;
    changeViewToNormal(); // first part of each stage is always normal view
    hideSubStages();

    const submitEvent = new CustomEvent('checkout-stage:changed', {
      detail: {
        currentStage,
      },
    });
    document.dispatchEvent(submitEvent);
  }
}

function setNextStage() {
  const newIndex = stagesArray.indexOf(getStage()) + 1;
  const nextStage = stagesArray[newIndex < stagesArray.length ? newIndex : stagesArray.length - 1];
  setStage(nextStage);
}

const blocksToNotVisibleSelector = '.header-no-menu__nav-row, .checkout-container__breadcrumbs, .checkout-container__summary';
const onlyFormBackBtnSelector = '.only-form-back-btn';
const slotPromoCheckoutSelector = '.slot-promo-checkout-message';

/* Removes visibility but preserves structure */
function changeViewToOnlyForm() {
  const checkoutMain = document.getElementById('checkout-main');
  if (checkoutMain) {
    checkoutMain.setAttribute('data-only-form-view', true);
  }
  hideElementsBySelector(slotPromoCheckoutSelector);
  showElementsBySelector(onlyFormBackBtnSelector);
  addVisibilityHiddenBySelector(blocksToNotVisibleSelector);
}

/* Restores visibility of normal checkout view */
function changeViewToNormal() {
  const checkoutMain = document.getElementById('checkout-main');
  if (checkoutMain) {
    checkoutMain.setAttribute('data-only-form-view', false);
  }
  showElementsBySelector(slotPromoCheckoutSelector);
  hideElementsBySelector(onlyFormBackBtnSelector);
  removeVisibilityHiddenBySelector(blocksToNotVisibleSelector);
}

module.exports = {
  stages,
  methods: {
    getStage,
    isStageFromPaymentGroup,
    isStageFromShippingGroup,
    setNextStage,
    setStage,
    changeViewToOnlyForm,
    changeViewToNormal,
  },
};
